/**
 * Formulaire de création d'une tâche, destiné à être rendu dans un `Modal`.
 *
 * Même contrat que `ProjectForm` : le composant ne poste rien, il collecte et délègue à
 * `onSubmit`. La seule requête qu'il fait est en lecture — la liste des colonnes du projet,
 * chargée ici plutôt que reçue en prop pour que la page projet n'ait pas à porter un état
 * que la vue Kanban gère déjà de son côté.
 *
 * Volontairement réduit à l'essentiel : titre, colonne, priorité, dates. Description, type,
 * assigné, charge et étiquettes se règlent dans le `TaskDrawer`, qui sait déjà le faire —
 * dupliquer tout l'éditeur dans un formulaire de création ne ferait que deux endroits à
 * maintenir pour le même contrat.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TASK_PRIORITIES } from '@exiplan/shared-types';
import { api } from '../lib/api.js';
import { fetchAllPages } from '../lib/pagination.js';
import { useAsync } from '../hooks/useAsync.js';
import { LoadError, TruncatedListWarning } from './common.jsx';
import { BTN_GHOST, BTN_PRIMARY, FORM_LABEL, INPUT, SELECT } from '../ui/theme.js';

// Libellé de champ exiUI (FORM_LABEL, 1.4.0) : le même que Champ/Selecteur et que toutes les
// lignes de formulaire du produit — deux libellés d'une même ligne ont le même style et le même haut.
const LABEL = `${FORM_LABEL} mb-1`;

export function TaskForm({
  projectId,
  defaultColumnId = '',
  defaultParentId = '',
  onSubmit,
  onCancel,
  submitting = false,
}) {
  const { t } = useTranslation();
  const {
    data: columns,
    error: columnsError,
    reload: reloadColumns,
  } = useAsync(
    (signal) =>
      api.get(`/projects/${projectId}/columns`, { signal }).then((response) => response.data ?? []),
    [projectId],
  );

  /*
   * Les lots sélectionnables sont chargés ici, comme les colonnes juste au-dessus, et non
   * reçus en prop : la page projet n'a pas à porter un état que ce formulaire est seul à
   * consommer. Aucune route nouvelle — `kind` est un filtre déjà servi par `GET /tasks`.
   */
  const {
    data: phasesPage,
    error: phasesError,
    reload: reloadPhases,
  } = useAsync(
    (signal) => fetchAllPages(`/projects/${projectId}/tasks?kind=phase`, { signal }),
    [projectId],
  );
  const phases = phasesPage?.data;

  /*
   * Mêmes raisons que `columns` et `phases` ci-dessus. La liste des membres du PROJET (pas de
   * l'équipe) est la bonne granularité d'accès à proposer — `ShareProjectDialog` consomme déjà
   * cette route, on ne réinvente pas un second chemin.
   */
  const {
    data: membersPage,
    error: membersError,
    reload: reloadMembers,
  } = useAsync(
    (signal) => fetchAllPages(`/projects/${projectId}/members`, { signal }),
    [projectId],
  );
  const members = membersPage?.data;

  // Un échec de chargement ne doit pas laisser des listes vides sans rien dire : on propose de
  // relancer uniquement ce qui a échoué.
  const loadError = columnsError ?? phasesError ?? membersError;
  const retryLoad = () => {
    if (columnsError) reloadColumns();
    if (phasesError) reloadPhases();
    if (membersError) reloadMembers();
  };

  const [values, setValues] = useState({
    title: '',
    columnId: defaultColumnId,
    parentId: defaultParentId,
    priority: 'medium',
    assigneeId: '',
    startDate: '',
    dueDate: '',
  });
  const [error, setError] = useState(null);

  const set = (field) => (event) => setValues((v) => ({ ...v, [field]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();

    const title = values.title.trim();
    if (!title) return setError(t('taskForm.errors.titleRequired'));
    // Comparaison lexicographique : les dates sont des chaînes ISO `AAAA-MM-JJ`, dont
    // l'ordre alphabétique est l'ordre chronologique. Le serveur accepterait l'inversion ;
    // la refuser ici évite un Gantt à barre négative que personne ne comprend ensuite.
    if (values.startDate && values.dueDate && values.dueDate < values.startDate) {
      return setError(t('taskForm.errors.datesInverted'));
    }
    setError(null);

    /*
     * Les champs vides sont **omis**, jamais envoyés à `''` : le schéma Zod attend une date
     * ISO ou rien, et `columnId` un UUID ou rien. Une chaîne vide est refusée en 400 par le
     * serveur — l'erreur serait exacte mais incompréhensible pour qui a simplement laissé
     * une date de côté.
     */
    const payload = { title, priority: values.priority };
    if (values.columnId) payload.columnId = values.columnId;
    if (values.parentId) payload.parentId = values.parentId;
    if (values.assigneeId) payload.assigneeId = values.assigneeId;
    if (values.startDate) payload.startDate = values.startDate;
    if (values.dueDate) payload.dueDate = values.dueDate;

    try {
      await onSubmit(payload);
    } catch (submitError) {
      setError(submitError.message || t('taskForm.errors.saveFailed'));
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <div>
        <label className={LABEL} htmlFor="task-title">
          {t('taskForm.title')}
        </label>
        <input
          id="task-title"
          className={INPUT}
          value={values.title}
          onChange={set('title')}
          placeholder={t('taskForm.titlePlaceholder')}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={LABEL} htmlFor="task-column">
            {t('taskForm.column')}
          </label>
          <select
            id="task-column"
            className={SELECT}
            value={values.columnId}
            onChange={set('columnId')}
          >
            <option value="">{t('taskForm.noColumn')}</option>
            {(columns ?? []).map((column) => (
              <option key={column.id} value={column.id}>
                {column.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={LABEL} htmlFor="task-priority">
            {t('taskForm.priority')}
          </label>
          <select
            id="task-priority"
            className={SELECT}
            value={values.priority}
            onChange={set('priority')}
          >
            {TASK_PRIORITIES.map((priority) => (
              <option key={priority} value={priority}>
                {t(`priority.${priority}`)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className={LABEL} htmlFor="task-parent">
          {t('taskForm.parent')}
        </label>
        <select
          id="task-parent"
          className={SELECT}
          value={values.parentId}
          onChange={set('parentId')}
        >
          <option value="">{t('taskForm.noParent')}</option>
          {(phases ?? []).map((phase) => (
            <option key={phase.id} value={phase.id}>
              {phase.title}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className={LABEL} htmlFor="task-assignee">
          {t('taskForm.assignee')}
        </label>
        <select
          id="task-assignee"
          className={SELECT}
          value={values.assigneeId}
          onChange={set('assigneeId')}
        >
          <option value="">{t('taskForm.noAssignee')}</option>
          {(members ?? []).map((member) => (
            <option key={member.userId} value={member.userId}>
              {member.name || member.email}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={LABEL} htmlFor="task-start">
            {t('taskForm.startDate')}
          </label>
          <input
            id="task-start"
            type="date"
            className={INPUT}
            value={values.startDate}
            onChange={set('startDate')}
          />
        </div>
        <div>
          <label className={LABEL} htmlFor="task-due">
            {t('taskForm.dueDate')}
          </label>
          <input
            id="task-due"
            type="date"
            className={INPUT}
            value={values.dueDate}
            onChange={set('dueDate')}
          />
        </div>
      </div>

      {loadError && <LoadError error={loadError} onRetry={retryLoad} />}
      {(phasesPage?.truncated || membersPage?.truncated) && <TruncatedListWarning />}

      {error && (
        <p role="alert" className="text-sm text-accent-ink">
          {error}
        </p>
      )}

      <div className="mt-2 flex justify-end gap-2">
        <button type="button" className={BTN_GHOST} onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button type="submit" className={BTN_PRIMARY} disabled={submitting}>
          {t('taskForm.create')}
        </button>
      </div>
    </form>
  );
}
